import React, { FC, useMemo } from "react";
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  FormControl,
  TextField,
  Tooltip,
} from "@mui/material";
import styles from "./MultiSelectField.module.css";
import { MultiSelectOption } from "./MultiSelectOptions";

interface MultiSelectCheckProps {
  label: string;
  value: Array<number | string>;
  onChange: (value: Array<number | string>) => void;
  options: MultiSelectOption[];
  required?: boolean;
  fullWidth?: boolean;
  placeholder?: string;
  error?: boolean;
  helperText?: React.ReactNode;
  selectedDisplayMode?: "count" | "tags";
  paperClassname?: string;
  popperClassname?: string;
  titleInputClassname?: string;
  inputSelectClassname?: string;
  labelSelectClassname?: string;
  summaryChipClassname?: string;
  disabled?: boolean;
}

const SELECT_ALL_VALUE = "__SELECT_ALL__";
const SELECT_ALL_LABEL = "Selecionar todos";

const isSelectAllOption = (option: string | MultiSelectOption) =>
  option === SELECT_ALL_VALUE;

const isMultiSelectOption = (
  option: string | MultiSelectOption,
): option is MultiSelectOption => typeof option !== "string";

const MultiSelectCheck: FC<MultiSelectCheckProps> = ({
  label,
  value,
  onChange,
  options,
  required,
  fullWidth = true,
  placeholder = "",
  error,
  helperText,
  selectedDisplayMode = "count",
  paperClassname,
  popperClassname,
  titleInputClassname,
  inputSelectClassname,
  labelSelectClassname,
  summaryChipClassname,
  disabled = false,
}) => {
  const optionIds = useMemo(() => options.map((option) => option.id), [options]);
  const allSelected = optionIds.length > 0 && optionIds.every((option) => value.includes(option));
  const normalizedValue = useMemo(
    () => options.filter((option) => value.includes(option.id)),
    [options, value],
  );

  const autocompleteOptions = useMemo(
    () => [SELECT_ALL_VALUE, ...options],
    [options],
  );
  const selectedNamesText = useMemo(
    () => normalizedValue.map((option) => option.nome).join(", "),
    [normalizedValue],
  );

  const handleChange = (_: React.SyntheticEvent, newValue: Array<string | MultiSelectOption>) => {
    const clickedSelectAll = newValue.some((item) => item === SELECT_ALL_VALUE);

    if (clickedSelectAll) {
      onChange(allSelected ? [] : optionIds);
      return;
    }

    onChange(
      newValue
        .filter((item): item is MultiSelectOption => item !== SELECT_ALL_VALUE)
        .map((item) => item.id),
    );
  };

  return (
    <FormControl fullWidth={fullWidth}>
      <Autocomplete
        multiple
        disableCloseOnSelect
        options={autocompleteOptions}
        value={normalizedValue}
        onChange={handleChange}
        disabled={disabled}
        filterSelectedOptions={false}
        slotProps={{
          paper: {
            className: paperClassname || styles.paper,
          },
          popper: {
            className: popperClassname || styles.popper,
          },
        }}
        getOptionLabel={(option) =>
          isMultiSelectOption(option) ? option.nome : SELECT_ALL_LABEL
        }
        renderOption={(props, option, { selected }) => {
          const isSelectAll = isSelectAllOption(option);
          const checked = isSelectAll ? allSelected : selected;

          return (
            <li {...props}>
              <Checkbox
                size="small"
                checked={checked}
                indeterminate={isSelectAll && value.length > 0 && !allSelected}
              />
              {isMultiSelectOption(option) ? option.nome : SELECT_ALL_LABEL}
            </li>
          );
        }}
        renderInput={(params) => (
          <>
            <label className={titleInputClassname || styles.titleInput}>{label}</label>
            <TextField
              {...params}
              label=""
              variant="standard"
              required={required}
              fullWidth={fullWidth}
              error={error}
              helperText={helperText}
              placeholder={value.length === 0 ? placeholder : ""}
              InputProps={{
                ...params.InputProps,
                className: inputSelectClassname || styles.inputSelect,
                disableUnderline: true,
              }}
              InputLabelProps={{
                className: labelSelectClassname || styles.labelSelect,
                shrink: true,
              }}
            />
          </>
        )}
        renderTags={(selected, getTagProps) => (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.2 }}>
            {selectedDisplayMode === "tags" ? (
              selected
                .filter(isMultiSelectOption)
                .map((option, index) => (
                  <Chip {...getTagProps({ index })} key={option.id} label={option.nome} />
                ))
            ) : selectedNamesText ? (
              <Tooltip
                title={selectedNamesText}
                placement="top"
                arrow
              >
                <Chip
                  {...getTagProps({ index: 0 })}
                  className={summaryChipClassname || styles.summaryChip}
                  label={`${normalizedValue.length} ${normalizedValue.length === 1 ? "registro selecionado" : "registros selecionados"}`}
                />
              </Tooltip>
            ) : null}
          </Box>
        )}
      />
    </FormControl>
  );
};

export default MultiSelectCheck;
